import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Button, Label, UncontrolledTooltip } from "reactstrap";
import moment from "moment";
import jsPDF from "jspdf";

import "../../styles/StackPlots.css";
import { default as utilIcons } from "../../resources/utilIcons";
import { colors } from "../../styles/colors";

import StackPlot from "./StackPlot";
import { data as dummyData, config as dummyConfig } from "./StackPlotDummy";
import { RestContext } from "../../App";
import { DATA_URL, STACKPLOT_URL } from "../../serverConfig";
import { checkMobile, isNumeric } from "../../util/globals";
import SearchableSelect from "../../components/SearchableSelect";

const INTERVALS = { // Number of seconds between data ticks
    "hourly": 60 * 60, // 1 hour
    "daily": 60 * 60, // 1 hour
    "cumulative": 60 * 60, // 1 hour
    "24-hour run": 60 * 60, // 1 hour
    "1-min": 60, // 1 minute
    "5-min": 5 * 60, // 5 minutes
    "15-min": 15 * 60, // 15 minutes
}

function StackPlots({ selectedSite }) {
    const [columns, setColumns] = useState([]);
    const [data, setData] = useState([]);
    const [config, setConfig] = useState();
    const [stackplotData, setStackplotData] = useState({});
    const [configList, setConfigList] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState();
    const [selectedOptions, setSelectedOptions] = useState({
        selectedSite: selectedSite,
        configId: null,
        precision: "hourly",
        datesToLoad: { minTimestamp: null, maxTimestamp: null },
        sameConfig: false,
    });
    const [loading, setLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(1);
    const [dateRange, setDateRange] = useState({});
    const [printMode, setPrintMode] = useState(false);
    const [chartRefs, setChartRefs] = useState();
    const [callbacks, setCallbacks] = useState({});

    const { sendGetRequest, sendPostRequest } = useContext(RestContext);

    const loadingOptions = useRef(loading);
    const stackPlotRef = useRef(null);

    const togglePrintMode = () => setPrintMode(!printMode);

    useEffect(() => {
        if (config) {
            const today = moment().hour(0).minute(0).second(0).millisecond(0);
            setSelectedOptions((values) => ({
                ...values,
                precision: config.precision || "hourly",
                configId: config.stackPlotId,
                datesToLoad: {
                    minTimestamp: moment(today)
                        .subtract(config.defaultSpan || 7, "day")
                        .unix(),
                    maxTimestamp: moment().unix(),
                },
                sameConfig: false,
            }));
        }
    }, [config]);

    useEffect(() => {
        if (config && selectedOptions) {
            if (selectedSite === "all") {
                const responseData = dummyData;
                setColumns(responseData.columns);
                setData(responseData.data);
            } else {
                beginLoad();
            }
        }
    }, [selectedOptions]);

    useEffect(() => {
        if (selectedSite !== "all") {
            setSelectedOptions((values) => ({
                ...values,
                selectedSite: selectedSite,
            }));
            sendGetRequest(
                STACKPLOT_URL + "/list/" + selectedSite,
                (response) => {
                    const responseData = response.data;
                    responseData.sort((a, b) =>
                        a.plotName > b.plotName ? 1 : -1
                    );
                    setConfigList(responseData);
                    if (responseData && responseData.length) {
                        setSelectedConfig(responseData[0].stackPlotId);
                    } else {
                        setSelectedConfig();
                    }
                }
            );
        } else {
            setConfigList([]);
            setConfig(dummyConfig);
        }
    }, [selectedSite]);

    useEffect(() => {
        if (selectedConfig) {
            sendGetRequest(
                STACKPLOT_URL + "/config/" + selectedConfig,
                (response) => {
                    setConfig(response.data);
                }
            );
        }
    }, [selectedConfig]);

    useEffect(() => {
        if (loading) {
            loadInfo();
        } else {
            setLoadingProgress(0);
        }
    }, [loading]);

    useEffect(() => {
        setStackplotData(explodeData(data));
    }, [data, selectedOptions]);

    useEffect(() => {
        setDateRange({ min: getMinDate(), max: getMaxDate() });
    }, [stackplotData]);

    const beginLoad = (dataOptions) => {
        if (!selectedOptions || !config || !config.parCodes) return;
        if (!dataOptions) {
            dataOptions = {
                selectedSite: selectedOptions.selectedSite,
                precision: selectedOptions.precision,
                sameConfig: selectedOptions.sameConfig,
                loadedMin: selectedOptions.sameConfig ? getLoadedMin() : null,
                loadedMax: selectedOptions.sameConfig ? getLoadedMax() : null,
                minTimestamp: selectedOptions.datesToLoad.minTimestamp,
                maxTimestamp: selectedOptions.datesToLoad.maxTimestamp,
                dataType: config.dataType,
                parCodes: config.parCodes,
                calPars: config.calPars,
            };
        }
        if (!loading) {
            setLoading(dataOptions);
        }
    };

    const reloadData = () => {
        if (!selectedOptions || !config || !config.parCodes || !config.calPars)
            return;
        const dataOptions = {
            selectedSite: selectedOptions.selectedSite,
            precision: selectedOptions.precision,
            sameConfig: selectedOptions.sameConfig,
            loadedMin: null,
            loadedMax: null,
            minTimestamp: selectedOptions.datesToLoad.minTimestamp,
            maxTimestamp: selectedOptions.datesToLoad.maxTimestamp,
            dataType: config.dataType,
            parCodes: config.parCodes,
            calPars: config.calPars,
        };
        beginLoad(dataOptions);
    };

    const loadInfo = () => {
        if (!selectedOptions) return;
        loadingOptions.current = loading;
        if (loading) {
            if (
                loading.loadedMin &&
                loading.loadedMin < selectedOptions.datesToLoad.minTimestamp
            ) {
                setLoading(null);
                return;
            }
            if (["RAWCAL", "VALCAL"].includes(config.dataType)) {
                const payload = {
                    precision: loading.precision,
                    minTimestamp: loading.minTimestamp,
                    maxTimestamp: loading.maxTimestamp,
                    loadedMin: loading.loadedMin,
                    loadedMax: loading.loadedMax,
                    dataType: loading.dataType,
                    calPars: loading.calPars,
                };
                sendPostRequest(
                    `${DATA_URL}/stackplot/info/${loading.selectedSite}`,
                    payload,
                    (response) => {
                        const numPages = response.data.numPages;
                        const pageSize = response.data.pageSize;
                        setLoadingProgress(0);
                        if (numPages > 0) {
                            setColumns(response.data.columns);
                            if (loading.sameConfig) {
                                loadPage(1, numPages, pageSize, loading, data);
                            } else {
                                loadPage(1, numPages, pageSize, loading);
                            }
                        } else {
                            if (!loading.sameConfig) {
                                setColumns([]);
                                setData({});
                            }
                            setLoading(null);
                        }
                    },
                    (error) => {
                        setLoading(null);
                    }
                );
            } else {
                const payload = {
                    precision: loading.precision,
                    minTimestamp: loading.minTimestamp,
                    maxTimestamp: loading.maxTimestamp,
                    loadedMin: loading.loadedMin,
                    loadedMax: loading.loadedMax,
                    dataType: loading.dataType,
                    parCodes: loading.parCodes,
                };
                sendPostRequest(
                    `${DATA_URL}/stackplot/info/${loading.selectedSite}`,
                    payload,
                    (response) => {
                        const numPages = response.data.numPages;
                        const pageSize = response.data.pageSize;
                        setLoadingProgress(0);
                        if (numPages > 0) {
                            setColumns(response.data.columns);
                            if (loading.sameConfig) {
                                loadPage(1, numPages, pageSize, loading, data);
                            } else {
                                loadPage(1, numPages, pageSize, loading);
                            }
                        } else {
                            if (!loading.sameConfig) {
                                setColumns([]);
                                setData({});
                            }
                            setLoading(null);
                        }
                    },
                    (error) => {
                        setLoading(null);
                    }
                );
            }
        }
    };

    const loadPage = (
        pageNumber,
        numPages,
        pageSize,
        dataOptions,
        tempData = {}
    ) => {
        if (
            JSON.stringify(loadingOptions.current) ===
            JSON.stringify(dataOptions)
        ) {
            if (["RAWCAL", "VALCAL"].includes(config.dataType)) {
                const payload = {
                    stackplotId: selectedConfig,
                    precision: dataOptions.precision,
                    page: pageNumber,
                    pageSize: pageSize,
                    numPages: numPages,
                    loadedMin: dataOptions.loadedMin,
                    loadedMax: dataOptions.loadedMax,
                    minTimestamp: dataOptions.minTimestamp,
                    maxTimestamp: dataOptions.maxTimestamp,
                    dataType: dataOptions.dataType,
                    calPars: dataOptions.calPars,
                };
                sendPostRequest(
                    `${DATA_URL}/stackplot/cal/${selectedSite}`,
                    payload,
                    (response) => {
                        if (
                            JSON.stringify(loadingOptions.current) ===
                            JSON.stringify(dataOptions)
                        ) {
                            const responseData = response.data;

                            tempData = { ...tempData, ...responseData.data };
                            setData(tempData);
                            setLoadingProgress(pageNumber / numPages);
                            if (pageNumber < numPages) {
                                loadPage(
                                    pageNumber + 1,
                                    numPages,
                                    pageSize,
                                    dataOptions,
                                    tempData
                                );
                            } else {
                                setLoading(null);
                                if (!selectedOptions.sameConfig) {
                                    setSelectedOptions((values) => ({
                                        ...values,
                                        sameConfig: true,
                                    }));
                                }
                            }
                        }
                    },
                    (error) => {
                        setLoading(null);
                    }
                );
            } else {
                const payload = {
                    stackplotId: selectedConfig,
                    precision: dataOptions.precision,
                    page: pageNumber,
                    pageSize: pageSize,
                    numPages: numPages,
                    loadedMin: dataOptions.loadedMin,
                    loadedMax: dataOptions.loadedMax,
                    minTimestamp: dataOptions.minTimestamp,
                    maxTimestamp: dataOptions.maxTimestamp,
                    dataType: dataOptions.dataType,
                    parCodes: dataOptions.parCodes,
                };
                sendPostRequest(
                    `${DATA_URL}/stackplot/${selectedSite}`,
                    payload,
                    (response) => {
                        if (
                            JSON.stringify(loadingOptions.current) ===
                            JSON.stringify(dataOptions)
                        ) {
                            const responseData = response.data;

                            tempData = { ...tempData, ...responseData.data };
                            setData(tempData);
                            setLoadingProgress(pageNumber / numPages);
                            if (pageNumber < numPages) {
                                loadPage(
                                    pageNumber + 1,
                                    numPages,
                                    pageSize,
                                    dataOptions,
                                    tempData
                                );
                            } else {
                                setLoading(null);
                                if (!selectedOptions.sameConfig) {
                                    setSelectedOptions((values) => ({
                                        ...values,
                                        sameConfig: true,
                                    }));
                                }
                            }
                        }
                    },
                    (error) => {
                        setLoading(null);
                    }
                );
            }
        }
    };

    const getLoadedMin = () => {
        if (!data) return null;
        const timestamps = Object.keys(data);
        if (!timestamps.length) return null;
        return getMin(timestamps);
    };

    const getLoadedMax = () => {
        if (!data) return null;
        const timestamps = Object.keys(data);
        if (!timestamps.length) return null;
        return getMax(timestamps);
    };

    const getMinDate = () => {
        if (!stackplotData || !stackplotData._timestamps) return 0;
        return moment
            .unix(stackplotData._timestamps[0])
            .format("YYYY-MM-DDTHH:mm");
    };

    const getMaxDate = () => {
        if (!stackplotData || !stackplotData._timestamps) return 0;
        return moment
            .unix(
                stackplotData._timestamps[stackplotData._timestamps.length - 1]
            )
            .format("YYYY-MM-DDTHH:mm");
    };

    const getMin = (data) => {
        if (data.length === 0) return -Infinity;
        else if (data.length === 1) return data[0];
        else {
            let min = data[0];
            for (let i = 1; i < data.length; i++) {
                if (data[i] < min) min = data[i];
            }
            return min;
        }
    };

    const getMax = (data) => {
        if (data.length === 0) return Infinity;
        else if (data.length === 1) return data[0];
        else {
            let max = data[0];
            for (let i = 1; i < data.length; i++) {
                if (data[i] > max) max = data[i];
            }
            return max;
        }
    };

    const explodeData = (rawData) => {
        if (!config?.dataType || !rawData || !columns) return null;
        const explodedData = {};
        if (["RAWCAL", "VALCAL"].includes(config.dataType)) {
            for (let i = 0; i < columns.length; i++) {
                if (!Array.isArray(columns[i])) return null;
                if (!columns[i][0].match(/_(FLAG|CODE)$/)) {
                    explodedData[columns[i]] = {
                        columnIndex: i,
                    };
                }
            }
        } else {
            for (let i = 0; i < columns.length; i++) {
                if (Array.isArray(columns[i])) return null;
                if (!columns[i].match(/_(FLAG|CODE)$/)) {
                    explodedData[columns[i]] = {
                        columnIndex: i,
                    };
                }
            }
        }

        let minTimestamp = selectedOptions.datesToLoad.minTimestamp;
        let maxTimestamp = selectedOptions.datesToLoad.maxTimestamp;
        let timestamps = [];
        let interval = INTERVALS[config?.precision || "hourly"];
        for (let i = 0; i <= (maxTimestamp - minTimestamp) / interval; i++) {
            timestamps.push(minTimestamp + i * interval);
        }

        for (let timestamp of timestamps) {
            let row = rawData[timestamp];
            if (row) {
                for (let parCode in explodedData) {
                    explodedData[parCode][timestamp] = [
                        row[explodedData[parCode].columnIndex],
                        row[explodedData[parCode].columnIndex + 1],
                    ];
                }
            }
        }
        explodedData._timestamps = timestamps;
        return explodedData;
    };

    const doXScroll = (scrollOptions) => {
        if (
            !dateRange ||
            dateRange.min === undefined ||
            dateRange.max === undefined ||
            !stackplotData ||
            !stackplotData._timestamps ||
            stackplotData._timestamps.length < 2
        )
            return;
        const { direction, quantity, unit } = scrollOptions;
        let offset = 0;
        switch (direction) {
            case "left":
                let dataMin = moment.unix(stackplotData._timestamps[0]);
                let currentMin = moment(dateRange.min, "YYYY-MM-DDTHH:mm");
                let targetMin = moment(currentMin).subtract(quantity, unit);
                if (targetMin.unix() < dataMin.unix()) {
                    offset = currentMin.unix() - dataMin.unix();
                } else {
                    offset = currentMin.unix() - targetMin.unix();
                }
                setDateRange({
                    min: currentMin
                        .subtract(offset, "seconds")
                        .format("YYYY-MM-DDTHH:mm"),
                    max: moment(dateRange.max, "YYYY-MM-DDTHH:mm")
                        .subtract(offset, "seconds")
                        .format("YYYY-MM-DDTHH:mm"),
                });
                return;
            case "right":
                let dataMax = moment.unix(
                    stackplotData._timestamps[
                        stackplotData._timestamps.length - 1
                    ]
                );
                let currentMax = moment(dateRange.max, "YYYY-MM-DDTHH:mm");
                let targetMax = moment(currentMax).add(quantity, unit);
                if (targetMax.unix() > dataMax.unix()) {
                    offset = dataMax.unix() - currentMax.unix();
                } else {
                    offset = targetMax.unix() - currentMax.unix();
                }
                setDateRange({
                    min: moment(dateRange.min, "YYYY-MM-DDTHH:mm")
                        .add(offset, "seconds")
                        .format("YYYY-MM-DDTHH:mm"),
                    max: currentMax
                        .add(offset, "seconds")
                        .format("YYYY-MM-DDTHH:mm"),
                });
                return;
            default:
                return;
        }
    };

    const updateTimeframe = (minTimestamp, maxTimestamp) => {
        setSelectedOptions((values) => ({
            ...values,
            datesToLoad: {
                minTimestamp: minTimestamp,
                maxTimestamp: maxTimestamp,
            },
        }));
    };

    const handleConfigSelect = (value) => {
        const plot = configList.find((p) => p.stackPlotId === value);
        setSelectedConfig(value);
    };

    const handleMinDate = (event) => {
        const value = event.target.value;
        setDateRange((values) => ({ ...values, min: value }));
    };

    const handleMaxDate = (event) => {
        const value = event.target.value;
        setDateRange((values) => ({ ...values, max: value }));
    };

    const clearCursors = () => {
        for (let pair of chartRefs) {
            const chart = pair[1].current;
            if (chart.options.plugins.annotation) {
                chart.options.plugins.annotation.annotations = [];
                chart.update();
            }
        }
    };

    const setChartDPI = (scale) => {
        if (chartRefs) {
            for (let index of chartRefs.keys()) {
                const chart = chartRefs.get(index)?.current || null;
                if (chart) {
                    chart.options.devicePixelRatio = scale;
                } else {
                    chartRefs.delete(index);
                }
            }
        }
    };

    const generatePdf = async () => {
        const foundStackPlot = stackPlotRef?.current;
        if (!foundStackPlot) return;
        const pages = foundStackPlot.querySelector(
            ".stackplot-pages-container"
        );
        const doc = new jsPDF({
            orientation: "portrait",
            format: [850, 1100], // 8.5 x 11 at 300 DPI
            unit: "px",
        });

        setChartDPI(3);

        await doc.html(pages, {
            x: 0,
            y: 0,
            autoPaging: "text",
        });

        setChartDPI(2);
        doc.deletePage(pages.children.length + 1); // Cleanup extra page created during loading
        return doc;
    };

    const downloadPdf = async () => {
        clearCursors();
        const doc = await generatePdf();
        doc.save(`${config?.fullTitle || ""}_${moment().format("MM-DD-YYYY")}`);
    };

    const printPdf = async () => {
        clearCursors();
        const doc = await generatePdf();
        doc.autoPrint({
            variant: navigator.userAgent.includes("Chrome")
                ? "non-conform"
                : "javascript",
        });
        window.open(
            doc.output(`bloburl`, {
                filename: `${config?.fullTitle || ""}_${moment().format(
                    "MM-DD-YYYY"
                )}`,
            })
        );
    };

    const registerCallback = (callbackName, callbackFn) => {
        setCallbacks((values) => ({ ...values, [callbackName]: callbackFn }));
    };

    return (
        <div id="stackplots">
            <div id="stackplots-controls">
                <div className="stackplot-control-wrapper">
                    <Label>Select Configuration:</Label>

                    <SearchableSelect
                        className={"stackplot-config-select"}
                        data={configList}
                        selected={selectedConfig}
                        setSelected={handleConfigSelect}
                        valField={"stackPlotId"}
                        textField={"plotName"}
                        groupField={"dataType"}
                    />
                </div>

                <div className="stackplot-control-wrapper">
                    <Label>Timeframe:</Label>
                    <StackPlotTimeFrameSelector
                        config={config}
                        updateTimeframe={updateTimeframe}
                    />
                </div>
                <div id="x-zoom-controls" className="stackplot-control-wrapper">
                    <Label>View:</Label>
                    <input
                        type="datetime-local"
                        onChange={handleMinDate}
                        min={getMinDate()}
                        max={dateRange.max || getMaxDate()}
                        step={
                            selectedOptions
                                ? selectedOptions.precision.match(/\d+-min/)
                                    ? 60
                                    : 3600
                                : 1
                        }
                        value={dateRange.min || ""}
                    />
                    <span>-</span>
                    <input
                        type="datetime-local"
                        onChange={handleMaxDate}
                        min={dateRange.min || getMinDate()}
                        max={getMaxDate()}
                        step={
                            selectedOptions
                                ? selectedOptions.precision.match(/\d+-min/)
                                    ? 60
                                    : 3600
                                : 1
                        }
                        value={dateRange.max || ""}
                    />
                    <DateScroller doXScroll={doXScroll} />
                </div>
                <Button
                    id="stackplots-print-mode-toggle"
                    onClick={togglePrintMode}
                >
                    {printMode ? "Expanded View" : "Print View"}
                </Button>
                <Button
                    id="stackplots-refresh-button"
                    onClick={reloadData}
                    disabled={loading}
                >
                    {utilIcons.refresh(colors["ars-neutral-900"])}
                </Button>
            </div>
            <div id="stackplot-controls-help">
                <div className="stackplot-controls-help-blurb">
                    <b>Drag:</b> Pan X Axis (all charts)
                </div>
                <div className="stackplot-controls-help-blurb">
                    <b>Ctrl+Scroll:</b> Zoom X Axis (all charts)
                </div>
                <div className="stackplot-controls-help-blurb">
                    <b>Shift+Drag</b> Pan Y Axis (single chart)
                </div>
                <div className="stackplot-controls-help-blurb">
                    <b>Shift+Scroll:</b> Zoom Y Axis (single chart)
                </div>
                <div className="stackplot-controls-help-blurb">
                    <b>Shift+Dbl Click:</b> Reset Y Axis (single chart)
                </div>
            </div>
            <StackPlot
                data={stackplotData}
                config={config}
                dateRange={dateRange}
                stackPlotRef={stackPlotRef}
                printable={printMode}
                bubbleChartRefs={setChartRefs}
                callbacks={callbacks}
            />
            <div id="stackplot-print-mode-buttons" hidden={!printMode}>
                <Button
                    className="stackplot-download-button"
                    onClick={downloadPdf}
                >
                    Download
                </Button>
                <Button
                    className="stackplot-download-button"
                    onClick={printPdf}
                >
                    Print
                </Button>
            </div>
            <StackPlotTable
                data={stackplotData}
                config={config}
                registerCallback={registerCallback}
            />
            <LoadingScreen
                hidden={!loading}
                loadingProgress={loadingProgress}
                isMobile={checkMobile()}
            />
        </div>
    );
}

function StackPlotTimeFrameSelector({ config, updateTimeframe }) {
    const [selectedMode, setSelectedMode] = useState("last");
    const [numDays, setNumDays] = useState(7);
    const [dateRange, setDateRange] = useState({ min: null, max: null });

    useEffect(() => {
        init();
    }, [config]);

    useEffect(() => {
        setDateRange({
            min: moment()
                .set("hour", 0)
                .set("minute", 0)
                .set("second", 0)
                .set("millisecond", 0)
                .subtract(numDays, "day")
                .format("YYYY-MM-DDTHH:mm"),
            max: moment().format("YYYY-MM-DDTHH:mm"),
        });
    }, [numDays]);

    const init = () => {
        setSelectedMode("last");

        setNumDays(config?.defaultSpan || 7);

        setDateRange({
            min: moment()
                .set("hour", 0)
                .set("minute", 0)
                .set("second", 0)
                .set("millisecond", 0)
                .add(-(config?.defaultSpan || 7), "day")
                .format("YYYY-MM-DDTHH:mm"),
            max: moment().format("YYYY-MM-DDTHH:mm"),
        });
    };

    const fireUpdate = (event) => {
        const allowedIds = [
            "stackplot-timeframe-numdays-input",
            "stackplot-timeframe-mindate-input",
            "stackplot-timeframe-maxdate-input",
            "stackplot-timeframe-mode-select",
        ];
        if (
            event.relatedTarget?.id &&
            allowedIds.includes(event.relatedTarget.id)
        )
            return;
        if (dateRange.min && dateRange.max) {
            updateTimeframe(
                moment(dateRange.min, "YYYY-MM-DDTHH:mm").unix(),
                moment(dateRange.max, "YYYY-MM-DDTHH:mm").unix()
            );
        }
    };

    const getControlsForMode = () => {
        switch (selectedMode) {
            case "last":
                return (
                    <>
                        <input
                            id="stackplot-timeframe-numdays-input"
                            type="number"
                            value={numDays}
                            min={1}
                            step={1}
                            onChange={handleNumDays}
                            onBlur={fireUpdate}
                        />
                        <span>Days</span>
                    </>
                );
            case "from":
                return (
                    <>
                        <input
                            id="stackplot-timeframe-mindate-input"
                            type="datetime-local"
                            value={dateRange.min}
                            onChange={handleMinDate}
                            step={3600}
                            onBlur={fireUpdate}
                        />
                        <span>to</span>
                        <input
                            id="stackplot-timeframe-maxdate-input"
                            type="datetime-local"
                            value={dateRange.max}
                            onChange={handleMaxDate}
                            onBlur={fireUpdate}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    const handleChangeMode = (event) => {
        const value = event.target.value;
        setSelectedMode(value);
        if (value === "last") {
            if (dateRange.min && dateRange.max) {
                const diff = Math.ceil(
                    moment(dateRange.max, "YYYY-MM-DDT-HH:mm").diff(
                        moment(dateRange.min, "YYYY-MM-DDT-HH:mm"),
                        "days"
                    )
                );
                setNumDays(diff);
            }
        } else {
            return;
        }
    };

    const handleNumDays = (event) => {
        setNumDays(Number(event.target.value));
    };

    const handleMinDate = (event) => {
        const value = event.target.value;
        setDateRange((values) => ({ ...values, min: value }));
    };

    const handleMaxDate = (event) => {
        const value = event.target.value;
        setDateRange((values) => ({ ...values, max: value }));
    };

    return (
        <div id="stackplot-timeframe-selector">
            <select
                id="stackplot-timeframe-mode-select"
                value={selectedMode}
                onChange={handleChangeMode}
            >
                <option value="last">Last</option>
                <option value="from">From</option>
            </select>
            {getControlsForMode()}
        </div>
    );
}

function DateScroller({ doXScroll }) {
    const [quantity, setQuantity] = useState(1);
    const [unit, setUnit] = useState("days");

    const handleQuantity = (event) => {
        const value = Number(event.target.value);
        setQuantity(value);
    };

    const handleUnit = (event) => {
        const value = event.target.value;
        setUnit(value);
    };

    const handleMinus = () => {
        doXScroll({
            direction: "left",
            quantity,
            unit,
        });
    };

    const handlePlus = () => {
        doXScroll({
            direction: "right",
            quantity,
            unit,
        });
    };

    return (
        <div className="date-scroller">
            <Button onClick={handleMinus}>-</Button>
            <input
                className="time-quantity-input"
                type="number"
                step={1}
                value={quantity}
                onChange={handleQuantity}
            />
            <select
                className="time-unit-select"
                value={unit}
                onChange={handleUnit}
            >
                <option value="hours">Hour(s)</option>
                <option value="days">Day(s)</option>
                <option value="weeks">Week(s)</option>
                <option value="months">Month(s)</option>
            </select>
            <Button onClick={handlePlus}>+</Button>
        </div>
    );
}

function StackPlotTable({ data, config, registerCallback }) {
    const [tableData, setTableData] = useState({});
    const [size, setSize] = useState("minimized");
    const [highlightedData, setHighlightedData] = useState({
        chartIndex: null,
        min: null,
        max: null,
    });

    const tableRef = useRef(null);

    useEffect(() => {
        registerCallback("cursor", setHighlightedData);
    }, []);

    useEffect(() => {
        if (data) {
            setTableData(processData(data));
        }
    }, [data]);

    useEffect(() => {
        if (
            highlightedData.min !== null &&
            tableRef.current &&
            size !== "minimized"
        ) {
            const highlightedRow = tableRef.current.querySelector(
                "#stackplot-table-row-" + highlightedData.min
            );
            if (highlightedRow) {
                highlightedRow.scrollIntoView({ block: "center" });
            }
        }
    }, [size, highlightedData]);

    const isCalPlot = () => {
        return ["RAWCAL", "VALCAL"].includes(config?.dataType || "");
    };

    const processData = (data) => {
        if (!config?.charts || !config?.calPars) return data;
        let newTableData = { ...data };
        if (Object.keys(data).some((key) => key.match(/DIFF/))) {
            newTableData = generateDiffDatasets(data);
        }
        return newTableData;
    };

    const generateDiffDatasets = (data) => {
        const newData = { ...data };
        for (let chart of config.charts) {
            let pDiffPar, diffPar;
            const parameterList = chart.parameters;
            if (
                (pDiffPar = parameterList.find(
                    (parameter) => parameter.valType === "PDIFF"
                ))
            ) {
                const isValidated = pDiffPar.parCode.match(/_VAL$/) !== null;
                const actualPar = parameterList.find((par) =>
                    isValidated
                        ? par.parCode.match(/_VAL$/) && par.valType === "ACTUAL"
                        : !par.parCode.match(/_VAL$/) &&
                          par.valType === "ACTUAL"
                );
                const indicatedPar = parameterList.find((par) =>
                    isValidated
                        ? par.parCode.match(/_VAL$/) &&
                          par.valType === "INDICATED"
                        : !par.parCode.match(/_VAL$/) &&
                          par.valType === "INDICATED"
                );

                if (actualPar && indicatedPar) {
                    const pDiffCalPar = [
                        pDiffPar.parCode,
                        pDiffPar.calType,
                        pDiffPar.valType,
                    ];
                    const actualCalPar = [
                        actualPar.parCode,
                        actualPar.calType,
                        actualPar.valType,
                    ];
                    const indicatedCalPar = [
                        indicatedPar.parCode,
                        indicatedPar.calType,
                        indicatedPar.valType,
                    ];
                    const actualData = data[actualCalPar],
                        indicatedData = data[indicatedCalPar];

                    if (actualData && indicatedData) {
                        newData[pDiffCalPar] = {};
                        Object.keys(actualData)
                            .sort()
                            .filter((key) => key !== "columnIndex")
                            .forEach((timestamp) => {
                                newData[pDiffCalPar][timestamp] =
                                    isNumeric(
                                        actualData[timestamp]
                                            ? actualData[timestamp][0]
                                            : null
                                    ) &&
                                    isNumeric(
                                        indicatedData[timestamp]
                                            ? indicatedData[timestamp][0]
                                            : null
                                    )
                                        ? (
                                              (100 *
                                                  (indicatedData[timestamp][0] -
                                                      actualData[
                                                          timestamp
                                                      ][0])) /
                                              actualData[timestamp][0]
                                          ).toFixed(2)
                                        : null;
                            });
                    }
                }
            } else if (
                (diffPar = parameterList.find(
                    (parameter) => parameter.valType === "DIFF"
                ))
            ) {
                const isValidated = diffPar.parCode.match(/_VAL$/) !== null;
                const actualPar = parameterList.find((par) =>
                    isValidated
                        ? par.parCode.match(/_VAL$/) && par.valType === "ACTUAL"
                        : !par.parCode.match(/_VAL$/) &&
                          par.valType === "ACTUAL"
                );
                const indicatedPar = parameterList.find((par) =>
                    isValidated
                        ? par.parCode.match(/_VAL$/) &&
                          par.valType === "INDICATED"
                        : !par.parCode.match(/_VAL$/) &&
                          par.valType === "INDICATED"
                );

                if (actualPar && indicatedPar) {
                    const diffCalPar = [
                        diffPar.parCode,
                        diffPar.calType,
                        diffPar.valType,
                    ];
                    const actualCalPar = [
                        actualPar.parCode,
                        actualPar.calType,
                        actualPar.valType,
                    ];
                    const indicatedCalPar = [
                        indicatedPar.parCode,
                        indicatedPar.calType,
                        indicatedPar.valType,
                    ];
                    const actualData = data[actualCalPar],
                        indicatedData = data[indicatedCalPar];

                    if (actualData && indicatedData) {
                        newData[diffCalPar] = {};
                        Object.keys(actualData)
                            .sort()
                            .filter((key) => key !== "columnIndex")
                            .forEach((timestamp) => {
                                newData[diffCalPar][timestamp] =
                                    isNumeric(
                                        actualData[timestamp]
                                            ? actualData[timestamp][0]
                                            : null
                                    ) &&
                                    isNumeric(
                                        indicatedData[timestamp]
                                            ? indicatedData[timestamp][0]
                                            : null
                                    )
                                        ? (
                                              indicatedData[timestamp][0] -
                                              actualData[timestamp][0]
                                          ).toFixed(actualPar.rawPrec)
                                        : null;
                            });
                    }
                }
            }
        }

        return newData;
    };

    const mapParameterListToHead = () => {
        if (isCalPlot()) return mapCalParListToHead();
        if (!config?.parCodes) return null;
        // const parCodes = config.parCodes.filter(
        //     (parCode) => parCode in tableData
        // );
        const parCodes = Object.keys(tableData).filter(
            (parCode) => parCode !== "_timestamps"
        );
        return (
            <tr>
                <th>Timestamp</th>
                {parCodes.map((parCode, index) => (
                    <>
                        <th key={`stackplot-table-head-${index}`}>{parCode}</th>
                    </>
                ))}
            </tr>
        );
    };

    const mapCalParListToHead = () => {
        if (!config.calPars) return null;
        const parCodeHead = [<th>Par Code</th>];
        const calTypeHead = [<th>Cal Type</th>];
        const valTypeHead = [<th>Val Type</th>];
        for (let calPar of config.calPars) {
            parCodeHead.push(<th>{calPar[0]}</th>);
            calTypeHead.push(<th>{calPar[1]}</th>);
            valTypeHead.push(<th>{calPar[2]}</th>);
            if (config.dataType === "VALCAL") {
                parCodeHead.push(<th>{calPar[0] + "_VAL"}</th>);
                calTypeHead.push(<th>{calPar[1]}</th>);
                valTypeHead.push(<th>{calPar[2]}</th>);
            }
        }
        return (
            <>
                <tr>{parCodeHead}</tr>
                <tr>{calTypeHead}</tr>
                <tr>{valTypeHead}</tr>
            </>
        );
    };

    const mapUnitListToHead = () => {
        if (isCalPlot()) return mapCalUnitListToHead();
        if (!config?.parCodes || !config?.unitCodes) return null;
        const unitCodes = [<th />];
        config.parCodes.forEach((parCode, index) => {
            if (parCode in tableData) {
                unitCodes.push(config.unitCodes[index]);
                if (config.dataType === "VAL") {
                    unitCodes.push(config.unitCodes[index]);
                }
            }
        });
        return (
            <tr>
                {unitCodes.map((unitCode, index) => (
                    <th key={`stackplot-table-head-unit-${index}`}>
                        {unitCode}
                    </th>
                ))}
            </tr>
        );
    };

    const mapCalUnitListToHead = () => {
        if (!config?.calPars || !config?.unitCodes) return null;
        // const unitCodes = config.unitCodes.filter(
        //     (unitCode, index) => config.calPars[index] in tableData
        // );
        const unitCodes = [];
        config.calPars.forEach((calPar, index) => {
            if (calPar in tableData) {
                unitCodes.push(config.unitCodes[index]);
                if (config.dataType === "VALCAL") {
                    unitCodes.push(config.unitCodes[index]);
                }
            }
        });
        return (
            <tr>
                <th>Timestamp</th>
                {unitCodes.map((unitCode, index) => (
                    <th key={`stackplot-table-head-unit-${index}`}>
                        {unitCode}
                    </th>
                ))}
            </tr>
        );
    };

    const isHighlighted = (rowIndex) => {
        if (
            !highlightedData ||
            highlightedData.min === null ||
            highlightedData.max === null
        )
            return false;
        return (
            rowIndex >= highlightedData.min && rowIndex <= highlightedData.max
        );
    };

    const flagString = (par, timestamp, dataObj) => {
        if (!dataObj) return "";
        const flag = dataObj.at(1);
        if (!flag) {
            return "";
        }
        if (flag.match(/[A-Z]+\(.*\)/)) {
            const code = flag.substring(0, flag.indexOf("("));
            const adj = flag.substring(flag.indexOf("(") + 1, flag.length - 1);
            const id = `${par.toString()}-${timestamp}-code`;
            return (
                <>
                    (
                    <span
                        id={id}
                        style={{
                            color: colors["ars-blue-400"],
                            textDecoration: "underline",
                        }}
                    >
                        {code}
                    </span>
                    )
                    <UncontrolledTooltip placement="top" target={id}>
                        {adj}
                    </UncontrolledTooltip>
                </>
            );
        } else {
            return dataObj.at(1) ? ` (${dataObj.at(1)})` : "";
        }
    };

    const mapRows = () => {
        if (isCalPlot()) return mapCalRows();
        if (!tableData || !config?.parCodes || !config?.charts) return null;
        // const parCodes = config.parCodes.filter(
        //     (parCode) => parCode in tableData
        // );
        const parCodes = Object.keys(tableData).filter(
            (parCode) => parCode !== "_timestamps"
        );
        const timestamps = tableData._timestamps;
        if (!timestamps) return null;

        const highlightedPars = [];
        if (highlightedData.chartIndex !== null) {
            const highlightedChart = config.charts[highlightedData.chartIndex];
            if (highlightedChart) {
                for (let parameter of highlightedChart.parameters) {
                    highlightedPars.push(parameter.parCode);
                    if (config.dataType === "VAL") {
                        highlightedPars.push(parameter.parCode + "_VAL");
                    }
                }
            }
        }

        const parCodePrec = {};
        for (let chart of config.charts) {
            for (let parameter of chart.parameters) {
                parCodePrec[parameter.parCode] = parameter.rawPrec;
            }
        }

        return timestamps.map((timestamp, index) => {
            const cells = [];
            cells.push(
                <td>{moment.unix(timestamp).format("MMM D hh:mm a")}</td>
            );
            for (let parCode of parCodes) {
                cells.push(
                    <>
                        <td
                            className={
                                highlightedPars.includes(parCode)
                                    ? "highlighted"
                                    : ""
                            }
                        >
                            {tableData[parCode][timestamp]
                                ? tableData[parCode][timestamp].at(0) === null
                                    ? "-"
                                    : tableData[parCode][timestamp]
                                          .at(0)
                                          .toFixed(parCodePrec[parCode])
                                : "-"}
                            {flagString(
                                parCode,
                                timestamp,
                                tableData[parCode][timestamp]
                            )}
                        </td>
                    </>
                );
            }
            return (
                <tr
                    key={`stackplot-table-row-${index}`}
                    id={`stackplot-table-row-${index}`}
                    className={
                        "stackplot-table-row" +
                        (isHighlighted(index) ? " highlighted-row" : "")
                    }
                >
                    {cells}
                </tr>
            );
        });
    };

    const mapCalRows = () => {
        if (!tableData || !config?.calPars?.length || !config?.charts)
            return null;
        const calPars = Object.keys(tableData)
            .filter((key) => key !== "_timestamps")
            .map((calPar) => calPar.split(","));
        if (!calPars.length) return null;
        const firstCalPar = config.calPars[0];
        if (!firstCalPar) return null;
        const firstCalDataset = tableData[firstCalPar];
        if (!firstCalDataset) return null;
        const timestamps =
            Object.keys(firstCalDataset)
                ?.filter((key) => key !== "columnIndex")
                ?.sort() || [];

        const highlightedPars = [];
        if (highlightedData.chartIndex !== null) {
            const highlightedChart = config.charts[highlightedData.chartIndex];
            if (highlightedChart) {
                for (let parameter of highlightedChart.parameters) {
                    highlightedPars.push([
                        parameter.parCode,
                        parameter.calType,
                        parameter.valType,
                    ]);
                }
            }
        }

        const calParPrec = {};
        for (let chart of config.charts) {
            for (let parameter of chart.parameters) {
                calParPrec[
                    [parameter.parCode, parameter.calType, parameter.valType]
                ] = parameter.rawPrec;
            }
        }

        const isHighlightedPar = (calPar) => {
            return highlightedPars.some((highlightedPar) => {
                for (let i = 0; i < highlightedPar.length; i++) {
                    if (highlightedPar[i] !== calPar[i]) return false;
                }
                return true;
            });
        };

        return timestamps.map((timestamp, index) => {
            const cells = [];
            cells.push(
                <td>{moment.unix(timestamp).format("MMM D hh:mm a")}</td>
            );
            for (let calPar of calPars) {
                if (calPar[2].match(/P?DIFF/)) {
                    cells.push(
                        <>
                            <td
                                className={
                                    isHighlightedPar(calPar)
                                        ? "highlighted"
                                        : ""
                                }
                            >
                                {tableData[calPar][timestamp] === null
                                    ? "-"
                                    : tableData[calPar][timestamp]}
                            </td>
                        </>
                    );
                } else {
                    cells.push(
                        <>
                            <td
                                className={
                                    isHighlightedPar(calPar)
                                        ? "highlighted"
                                        : ""
                                }
                            >
                                {tableData[calPar][timestamp]
                                    ? tableData[calPar][timestamp]?.at(0) ===
                                      null
                                        ? "-"
                                        : tableData[calPar][timestamp]
                                              .at(0)
                                              .toFixed(calParPrec[calPar])
                                    : "-"}
                                {flagString(
                                    calPar,
                                    timestamp,
                                    tableData[calPar][timestamp]
                                )}
                            </td>
                        </>
                    );
                }
            }
            return (
                <tr
                    key={`stackplot-table-row-${index}`}
                    id={`stackplot-table-row-${index}`}
                    className={
                        "stackplot-table-row" +
                        (isHighlighted(index) ? " highlighted-row" : "")
                    }
                >
                    {cells}
                </tr>
            );
        });
    };

    const convertToCsvString = () => {
        if (isCalPlot()) return convertCalToCsvString();
        if (!tableData || !config?.parCodes || !config?.charts) return null;
        const parCodes = [],
            unitCodes = [];
        for (let index in config.parCodes) {
            const parCode = config.parCodes[index];
            if (parCode in tableData) {
                parCodes.push(parCode);
                unitCodes.push(config.unitCodes[index]);
                if (config.dataType === "VAL") {
                    parCodes.push(parCode + "_VAL");
                    unitCodes.push(config.unitCodes[index]);
                }
            }
        }

        let csvString = "Date,Time";
        for (let parCode of parCodes) {
            csvString += `,${parCode},${parCode}_${
                parCode.match(/_VAL/) ? "CODE" : "FLAG"
            }`;
        }
        csvString += "\n,";
        for (let unitCode of unitCodes) {
            csvString += `,${unitCode},`;
        }
        csvString += "\n";

        const parCodePrec = {};
        for (let chart of config.charts) {
            for (let parameter of chart.parameters) {
                parCodePrec[parameter.parCode] = parameter.rawPrec;
            }
        }

        const timestamps = tableData._timestamps;
        for (let timestamp of timestamps) {
            const cells = [];
            cells.push(moment.unix(timestamp).format("YYYYMMDD,HHmm"));
            for (let parCode of parCodes) {
                cells.push(
                    tableData[parCode][timestamp]
                        ?.at(0)
                        ?.toFixed(parCodePrec[parCode])
                );
                cells.push(tableData[parCode][timestamp]?.at(1));
            }
            csvString += cells.join(",") + "\n";
        }
        return csvString;
    };

    const convertCalToCsvString = () => {
        if (!tableData || !config?.calPars || !config?.charts) return null;
        const calPars = [],
            unitCodes = [];
        for (let index in config.calPars) {
            const calPar = config.calPars[index];
            if (calPar in tableData) {
                calPars.push(calPar);
                unitCodes.push(config.unitCodes[index]);
                if (config.dataType === "VALCAL") {
                    const valCalPar = [...calPar];
                    valCalPar[0] = valCalPar[0] + "_VAL";
                    calPars.push(valCalPar);
                    unitCodes.push(config.unitCodes[index]);
                }
            }
        }
        let csvString = ",Par Code";
        for (let calPar of calPars) {
            csvString += `,${calPar[0]},${calPar[0]}_${
                calPar[0].match(/_VAL/) ? "CODE" : "FLAG"
            }`;
        }
        csvString += "\n,Cal Type";
        for (let calPar of calPars) {
            csvString += `,${calPar[1]},`;
        }
        csvString += "\n,Val Type";
        for (let calPar of calPars) {
            csvString += `,${calPar[2]},`;
        }
        csvString += "\nDate,Time";
        for (let unitCode of unitCodes) {
            csvString += `,${unitCode},`;
        }
        csvString += "\n";
        const timestamps = [];
        for (let calPar of calPars) {
            for (let timestamp in tableData[calPar]) {
                if (timestamp === "columnIndex") continue;
                if (!timestamps.includes(timestamp)) {
                    timestamps.push(timestamp);
                }
            }
        }
        timestamps.sort();

        const calParPrec = {};
        for (let chart of config.charts) {
            for (let parameter of chart.parameters) {
                calParPrec[
                    [parameter.parCode, parameter.calType, parameter.valType]
                ] = parameter.rawPrec;
            }
        }

        for (let timestamp of timestamps) {
            const cells = [];
            cells.push(moment.unix(timestamp).format("YYYYMMDD,HHmm"));
            for (let calPar of calPars) {
                if (Array.isArray(tableData[calPar][timestamp])) {
                    cells.push(
                        tableData[calPar][timestamp]
                            ?.at(0)
                            ?.toFixed(calParPrec[calPar])
                    );
                    cells.push(tableData[calPar][timestamp]?.at(1));
                } else {
                    cells.push(tableData[calPar][timestamp]);
                    cells.push("");
                }
            }
            csvString += cells.join(",") + "\n";
        }
        return csvString;
    };

    const exportCSV = () => {
        const file = new Blob([convertToCsvString()], { type: "text/csv" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(file);
        const dateString = moment().format("YYYYMMDD");
        const nameString =
            config?.plotName?.replace(/[\s\-]+/, "_") || "PLOT_DATA";
        link.href = url;
        link.download = [dateString, nameString, ".csv"].join("_");
        document.body.appendChild(link);
        link.click();
        setTimeout(function () {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }, 0);
    };

    return (
        <div id="stackplot-table-wrapper" className={size}>
            <div id="stackplot-table-controls">
                <span>Table View</span>
                <Button
                    id="stackplot-table-export"
                    onClick={exportCSV}
                    hidden={size === "minimized"}
                >
                    Export
                </Button>
                <Button
                    onClick={() => {
                        setSize("minimized");
                    }}
                >
                    {utilIcons.minimized(colors["ars-neutral-900"])}
                </Button>
                <Button
                    onClick={() => {
                        setSize("half");
                    }}
                >
                    {utilIcons.halfScreen(colors["ars-neutral-900"])}
                </Button>
                <Button
                    onClick={() => {
                        setSize("full");
                    }}
                >
                    {utilIcons.fullScreen(colors["ars-neutral-900"])}
                </Button>
            </div>
            <div id="stackplot-table">
                <table ref={tableRef}>
                    <thead>
                        {mapParameterListToHead()}
                        {mapUnitListToHead()}
                    </thead>
                    <tbody>{mapRows()}</tbody>
                </table>
            </div>
        </div>
    );
}

function LoadingScreen({ hidden, loadingProgress = 0, isMobile = false }) {
    const getContent = () => {
        if (isMobile) {
            return (
                <div className="loading-wrapper">
                    <progress value={loadingProgress} />
                    <span>{Math.round(loadingProgress * 1000) / 10}%</span>
                </div>
            );
        }
    };

    return (
        <div
            className={"loading" + (isMobile ? " mobile" : "")}
            hidden={hidden}
        >
            {getContent()}
        </div>
    );
}

export default StackPlots;
